<template>
  <content-subpage
    v-if="isReady"
    class="search-module-detail-content-subpage"
    :visible="visible"
    :has-parent-subpage="hasParentSubpage"
    :titles="titles"
    :show-content-title="false"
    :back-label="backLabel"
    @content-subpage-hidden="actionHidden(handleHidden())"
    @content-subpage-back="actionBack(handleBack())"
  >
    <template #title-buttons>
      <star-resource-button
        v-if="showStarResource"
        :resource-uuid="paramsData.uuid"
        :resource-type="paramsData.type"
        :resource-status="getStatus"
        @resource-unstarred="handleResourceUnstarred"
      />
    </template>
    <template #main-content>
      <lesson-preview :params="paramsData" :show-info-content="false" />

      <div class="info-header">
        <ellipsis-text class="info-title" :text="infoTitleLabel" />
      </div>

      <Tabs>
        <template #trigger>
          <tab-controls-item
            v-for="(tab, index) in tabs"
            :key="tab.key"
            :class="tabsCssVars(tab.key)"
            @click="handleTabSelected(index)"
          >
            <h5 class="title">{{ tab.title }}</h5>
          </tab-controls-item>
        </template>
        <template #controls>
          <tab-controls-item
            v-for="(tab, index) in tabs"
            :key="tab.key"
            :class="tabsCssVars(tab.key)"
            @click="handleTabSelected(index)"
          >
            <h5 class="title">{{ tab.title }}</h5>
          </tab-controls-item>
        </template>

        <template #content>
          <!-- Module Summary Tab -->
          <tab-content-item
            v-if="isTabSelected('module-summary')"
            key="module-summary"
            :class="tabsCssVars('module-summary')"
          >
            <search-module-detail-summary
              :params="paramsData"
              @game-book-selected="handleGameBookSelected"
            />
          </tab-content-item>

          <!-- Module Plan Tab -->
          <tab-content-item
            v-if="isTabSelected('module-plan')"
            key="module-plan"
            :class="tabsCssVars('module-plan')"
          >
            <search-module-detail-plan
              :params="paramsData"
              :selected-sections="selectedSections"
              :selected-activities="selectedActivities"
              :selection-mode="vm.selectionMode"
              :is-entire-module-selected="isEntireModuleSelected"
              @select-entire-module="handleSelectEntireModule"
              @update-selected-section="handleUpdateSelectedSection"
              @update-selected-activity="handleUpdateSelectedActivity"
              @section-toggle="handleSectionToggle"
            />
          </tab-content-item>
        </template>
      </Tabs>
    </template>
    <template #action-button>
      <fetch-data-button
        v-if="showChooseButton"
        v-bind="dataSaveButton"
        :disabled="isChooseButtonDisabled"
        :icon-only="false"
        tip-position="top"
        tip-alignment="end"
        @click="handleChoose"
        @reset-completed-state="handleSaveButtonResetCompleted"
        @reset-error-state="handleSaveButtonResetError"
      >
        <template v-if="dataSaveButton.error">
          {{ t("button_state_error") }}
        </template>
        <template v-else>
          <svg-icon name="Proceed32" kind="action" />
          <span>{{ t("search_module_detail_choose_button_label") }}</span>
        </template>
      </fetch-data-button>
    </template>
  </content-subpage>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { isDefined, isEmpty, isNull } from "@utils/var.check.ts";
import SearchModuleDetailSummary from "@modules/search/components/SearchModuleDetailSummary.vue";
import SearchModuleDetailPlan from "@modules/search/components/SearchModuleDetailPlan.vue";
import LessonPreview from "@plugins/resource/LessonPreview.vue";
import ObjectUtils from "@utils/object.utils.ts";
import {
  LESSON_ACTIVITY_TYPE,
  RESOURCE_DOMAIN_PATH,
} from "@plugins/common/constant/resource.constant.ts";
import {
  ATTEMPT_TYPE,
  ATTEMPTABLE_TYPE,
  RESOURCE_ATTEMPT_TYPE,
  RESOURCE_STATUS,
} from "@modules/search/service/search.constant.ts";
import store from "@plugins/course-lesson-plan/components/CoursePlanStore";
import { RESOURCE_DOMAIN, RESOURCE_TYPE } from "@modules/editor/service/editor.constant.ts";
import RuleConditionApi from "@apis/rule.condition.api.ts";
import LessonApi from "@apis/editor.lesson.api";
import SearchAttemptApi from "@apis/search.attempt.api.ts";
import AssignmentLessonApi from "@apis/assignment.lesson.api.ts";
import AssignmentApi from "@apis/assignment.api";
import MeetingLinkApi from "@apis/meeting.link.api.ts";
import AuthoringCopilotTemplateSelectionMixin from "@modules/editor/mixin/authoring.copilot.template.selection.mixin";
import EditorTemplateResourceSelectionMixin from "@modules/editor/mixin/editor.template.resource.selection.mixin";
import ResourceEditMixin from "@plugins/resource/mixin/resource.edit.mixin";
import DateTimeFormatter from "@utils/datetime.formatter";
import cgLessonApi from "@apis/cg.lesson.api.ts";
import { useContentSubpage } from "@plugins/content-subpage/composables/content-subpage.ts";
import { useGoogleDrive } from "@plugins/google/composables/google-drive.ts";
import { useProcessingState } from "@/modules/app/composables/processing-state";
import { useDirty } from "@plugins/common/composables/page-dirty.ts";
import { fetchWithSignedUrl } from "@utils/s3-signed-url.util.ts";
import StarResourceButton from "@plugins/common/components/StarResourceButton.vue";
import ContentSubpage from "@plugins/content-subpage/components/ContentSubpage.vue";
import EllipsisText from "@plugins/common/components/EllipsisText.vue";
import FetchDataButton from "@plugins/common/components/FetchDataButton.vue";
import { loadLocaleMessages } from "@plugins/i18n.ts";
import { useErrorHandler } from "@plugins/common/composables/error.ts";
import { useStore } from "vuex";
import { NAMESPACE } from "@modules/resource-search/resource-search-helper.ts";
import { useVm } from "@utils/vm.ts";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";
import { RULE_CONDITION_TYPE } from "@/modules/attempt/service/attempt.constant";
import {
  AddLessonFromLibraryContentSubpageSelectionEvent,
  LessonSelectionEventType,
  LessonSelectionModeEnum,
  LessonSelectionTypeEnum,
  SearchModuleDetail,
  SearchResourceModuleData,
} from "@modules/resource-search/resource-search-types.ts";
import { INTEGRATOR_APP_FEATURE_CATEGORY } from "@modules/app/service/app.constant.ts";
import sortUtils from "@utils/sort.utils";
import resourceApi from "@apis/resource.api";
import { useModuleAssignSubmitValidation } from "@modules/editor/composables/use-module-assign-submit-validation";
import { useModal } from "@plugins/modal/composables/modal-instance";

defineOptions({
  name: "SearchModuleDetailContentSubpage",
  mixins: [
    AuthoringCopilotTemplateSelectionMixin,
    EditorTemplateResourceSelectionMixin,
    ResourceEditMixin,
  ],
});

interface Props {
  params: SearchModuleDetail;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  selectionSelected: [value: string];
  resourceSelected: [];
  lessonTemplateChosen: [value: Record<string, unknown>];
  resourceUnstarred: [];
  selected: [data: AddLessonFromLibraryContentSubpageSelectionEvent];
}>();

const { t } = useI18n();
const vuexStore = useStore();
const { vm, vmExpose } = useVm();
const { passiveApiFailHandler } = useErrorHandler();
const { visible, titles, hasParentSubpage, actionHidden, actionBack, defaultExpose } =
  useContentSubpage(props.params);
const { checkIfHasGoogleDriveAccess, showAppAvailabilityWarningModal } = useGoogleDrive();
const { isProcessing, resetProcessing } = useProcessingState();
const { validateResourceAssignable, showAdaptiveSectionLoModal } =
  useModuleAssignSubmitValidation();
const modal = useModal();

const dirtyId = ref("search-module-detail-content-subpage");
const { isDirty, setDirty, removeDirty } = useDirty(dirtyId);

const isReady = ref(false);
const selectedTab = ref("module-plan");

const userId = computed(() => vuexStore.getters[`${NAMESPACE.AUTH}/userId`]);
const isStudent = computed<boolean>(() => vuexStore.getters[`${NAMESPACE.AUTH}/isStudent`]);
const isTeacher = computed<boolean>(() => vuexStore.getters["auth/isTeacher"]);

const serverTime = computed(() => vuexStore.state[NAMESPACE.APP].serverTime);

//from authoring.copilot.template.selection.mixin.ts
const isFromAuthoringCopilot = computed(() => {
  const acpAppCategory = [
    INTEGRATOR_APP_FEATURE_CATEGORY.ACP_MODULE,
    INTEGRATOR_APP_FEATURE_CATEGORY.ACP_ACTIVITY,
    INTEGRATOR_APP_FEATURE_CATEGORY.ACP_COMPONENT,
  ];
  return !isEmpty(props.params.appCategory) && acpAppCategory.includes(props.params.appCategory);
});
const isFromLessonModuleAuthoringCopilot = computed(
  () => INTEGRATOR_APP_FEATURE_CATEGORY.ACP_MODULE === props.params.appCategory,
);
const isFromLessonActivityAuthoringCopilot = computed(
  () => INTEGRATOR_APP_FEATURE_CATEGORY.ACP_ACTIVITY === props.params.appCategory,
);
const isFromLessonComponentAuthoringCopilot = computed(
  () => INTEGRATOR_APP_FEATURE_CATEGORY.ACP_COMPONENT === props.params.appCategory,
);

//from editor.template.resource.selection.mixin.ts
const isDefaultSelectionMode = computed(() => vm.isDefaultSelectionMode);
const inLessonEditor = computed(() => vm.isDefaultSelectionMode);
const addSectionsOrActivities = vm.addSectionsOrActivities;

const location = (loc: string) => {
  if (props.params.location) {
    return props.params.location;
  }
  switch (loc) {
    case "PERSONAL":
      return t("my_drive_label");
    case "MOE":
      return t("moe_library_label");
    case "COMMUNITY":
    case "Community":
      return t("community_gallery_label");
    case "ALP_TEMPLATE": {
      return t("alp_template_label");
    }
    default:
      return "-";
  }
};

const backLabel = computed(() => {
  const backLabelFinal = props.params.customBackLabel
    ? props.params.customBackLabel
    : titles.value.at(-3);
  return `${t("content_subpage_go_back_link")} ${backLabelFinal}`;
});

const paramsData = ref<SearchModuleDetail>({
  ...props.params,
  location: location(props.params.resourceDomain),
  moduleData: null,
});
const metaData = ref<Record<string, unknown>>({});
const sectionList = ref<unknown[]>([]);
const pageSettingsMap = ref<Record<string, unknown>>({});
const activityMeetingLinkStatusMap = ref(new Map());
const activityTeamMeetingLinkStatusMap = ref(new Map());
const sectionPrerequisites = ref<Record<string, unknown>>({});
const activityPrerequisites = ref<Record<string, unknown>>({});
const sequentialActivityMap = ref<Record<string, unknown>>({});
const previousCompletionPrerequisite = ref(false);
const overallProgress = ref({}); //for teacher
const overallPageProgressMap = ref<Record<string, unknown>>({}); //for teacher
const packageProgress = ref({}); //for student
const pageCompletionInfoMap = ref<Record<string, unknown>>({}); //for student
const selectedSections = ref<string[]>([]);
const selectedActivities = ref<string[]>([]);
const isEntireModuleSelected = ref(false);
const selectedGameBookId = ref<string | null>(null);
const dataSaveButton = ref({
  kind: "primary",
  size: "field",
  error: false,
  loading: false,
  completed: false,
  iconOnly: true,
  tipPosition: "bottom",
  errorMessage: t("api_fetch_generic_error_message"),
  iconName: "Save24",
});
const showSectionCover = ref(true);
//const parentSubpageName = ref("");
const resourceUnstarred = ref(false);

const isSelectingResource = computed(() => !!props.params.selectionType);

const showChooseButton = computed(() => isSelectingResource.value);

const lessonActivityList = computed(() => paramsData.value?.lesson?.lessonActivityList || []);

const hasSections = computed(() => sectionList.value?.length > 0);

const availableSectionIds = computed(() => sectionList.value.map((section) => String(section.id)));

const isMultipleSectionSelectionMode = computed(
  () => props.params.selectionMode === LessonSelectionModeEnum.multiple,
);

const hasSelectableSections = computed(() => hasSections.value && showSectionCover.value);

const infoTitleLabel = computed(() =>
  isSelectingResource.value && props.params.selectionType !== LessonSelectionTypeEnum.gamebook
    ? t("search_module_detail_title_label")
    : "",
);

const getActivitiesForSection = (sectionId: string) => {
  const activities = lessonActivityList.value.filter(
    (activity: unknown) => activity.parentId === sectionId,
  );

  const sortConfig = [{ field: "pageNo", asc: true }];
  const sortedActivities = sortUtils.sortObjects(activities, sortConfig);

  return sortedActivities;
};

const getSectionPrerequisite = (sectionId: string) => {
  return sectionPrerequisites.value[sectionId] || [];
};

const tabs = computed(() => [
  {
    key: "module-summary",
    title: t("module_summary_tab_label"),
  },
  {
    key: "module-plan",
    title: t("module_plan_tab_label"),
  },
]);

const getPageSetting = (pageId: string) => {
  return pageSettingsMap.value[pageId];
};

const getPageProgress = (sectionId: string) => {
  return (
    overallPageProgressMap.value[sectionId] || {
      id: sectionId,
      numStudentCompleted: 0,
      numPendingRelease: 0,
    }
  );
};

const getPageCompletionInfo = (sectionId: string) => {
  return pageCompletionInfoMap.value[sectionId] || { completed: false };
};

const isTabSelected = (value: string) => {
  return value.toString() === selectedTab.value;
};

const tabsCssVars = (tab: string) => {
  return { active: isTabSelected(tab) };
};

const isChooseButtonDisabled = computed(() => {
  if (dataSaveButton.value.loading || dataSaveButton.value.error) {
    return true;
  } else if (isFromLessonModuleAuthoringCopilot.value) {
    return false;
  } else if (isFromLessonActivityAuthoringCopilot.value) {
    return isEmpty(selectedSections.value);
  } else if (isFromLessonComponentAuthoringCopilot.value) {
    return isEmpty(selectedActivities.value);
  }

  if (props.params.selectionType === LessonSelectionTypeEnum.gamebook) {
    return isEmpty(selectedGameBookId.value);
  }

  if (props.params.selectionType === LessonSelectionTypeEnum.activityOrSection) {
    return isEmpty(selectedSections.value) && isEmpty(selectedActivities.value);
  }

  if (paramsData.value.isContextCreateAssignment) {
    return isProcessing.value;
  }

  if (props.params.selectionType === LessonSelectionTypeEnum.sectionOrLesson) {
    if (isMultipleSectionSelectionMode.value) {
      return hasSelectableSections.value
        ? !isEntireModuleSelected.value && isEmpty(selectedSections.value)
        : false;
    }
    return hasSections.value && showSectionCover.value ? isEmpty(selectedSections.value) : false;
  }

  if (props.params.selectionType === LessonSelectionTypeEnum.lesson) {
    return false;
  }

  if (paramsData.value.type === RESOURCE_TYPE.LESSON) {
    if (isDefaultSelectionMode.value && selectedActivities.value.length === 0) {
      return true;
    }
    return !isDirty(dirtyId.value) || isProcessing.value;
  }
  return false;
});

const isSection = (activityId: string) => {
  return lessonActivityList.value?.some(
    (section: unknown) =>
      section.id === activityId && section.type === LESSON_ACTIVITY_TYPE.SECTION,
  );
};

const isSequentialActivityCompletionPrerequisite = (sectionId: string) => {
  return sequentialActivityMap.value[sectionId] || false;
};

const isAlpResource = computed(() =>
  paramsData.value.lesson
    ? paramsData.value.lesson.resourceDomain === RESOURCE_DOMAIN.ALP_TEMPLATE.value
    : false,
);

const isCommunityResource = computed(
  () => paramsData.value.resourceDomain === RESOURCE_DOMAIN_PATH.COMMUNITY_GALLERY,
);

const getStatus = computed(() => {
  if (paramsData.value.lesson) {
    return paramsData.value.lesson.status ?? RESOURCE_STATUS.APPROVED;
  }
  return paramsData.value.status ? paramsData.value.status : RESOURCE_STATUS.APPROVED;
});

const showStarResource = computed(() => {
  if (paramsData.value.type === "ASSIGNMENT_LESSON") {
    return false;
  }

  if (isAlpResource.value) {
    return false;
  }

  return !isCommunityResource.value;
});

const moduleData = computed(() => {
  const sectionData = ObjectUtils.deepClone(sectionList.value);

  sectionData.forEach((section: unknown) => {
    section.sectionPrerequisite = getSectionPrerequisite(section.id);
    section.sequentialActivityCompletionReq = isSequentialActivityCompletionPrerequisite(
      section.id,
    );
    if (isTeacher.value) {
      section.progress = getPageProgress(section.id);
    } else if (isStudent.value) {
      section.statusInfo = getPageCompletionInfo(section.id);
    }
    section.settings = getPageSetting(section.id);
    const activityData = ObjectUtils.deepClone(getActivitiesForSection(section.id));

    activityData.forEach((activity: unknown) => {
      if (isTeacher.value) {
        activity.progress = getPageProgress(activity.id);
      } else if (isStudent.value) {
        activity.statusInfo = getPageCompletionInfo(activity.id);
      }
      activity.settings = getPageSetting(activity.id);
    });
    section.activityData = activityData;
  });
  return {
    ...paramsData.value.lesson,
    metaData: metaData.value,
    sequentialSectionCompletionReq: previousCompletionPrerequisite.value,
    overallProgress: isTeacher.value ? overallProgress.value : null,
    moduleAttemptStatus: isTeacher.value ? null : packageProgress.value,
    sectionData: sectionData,
  } as SearchResourceModuleData;
});

const assignmentAttemptRequestData = (sectionId: string | null) => {
  const requestData: Record<string, unknown> = {
    userId: userId.value,
    time: serverTime.value,
    attemptType: ATTEMPT_TYPE.ATTEMPT,
    attemptableType: paramsData.value.type,
    attemptableUuid: paramsData.value.assignmentUuid,
    resourceAttemptType: RESOURCE_ATTEMPT_TYPE.ASSIGNMENT,
    isAttachingEvent: true,
  };
  if (sectionId) {
    requestData.activityId = sectionId;
  }
  return requestData;
};

watch(
  showChooseButton,
  (newVal) => {
    if (!newVal) {
      selectedTab.value = "module-summary";
    }
  },
  { immediate: true },
);

onMounted(() => {
  if (props.params.selectionType === LessonSelectionTypeEnum.gamebook) {
    selectedTab.value = "module-summary";
  }
  titles.value.push(props.params.title);
  titles.value.push(t("search_course_detail_subpage_title"));

  // NOTE (SLS-12428): the Adaptive-Section-LO blocking modal's copy keys
  // (adaptive_section_lo_validation_modal_*) live in the "editor.course" locale namespace,
  // which is not loaded by default here — load it so the modal doesn't show raw i18n keys.
  loadLocaleMessages("editor.course");

  if (isFromLessonActivityAuthoringCopilot.value) {
    vm.selectionMode = LESSON_ACTIVITY_TYPE.SECTION;
  } else if (isFromLessonComponentAuthoringCopilot.value) {
    vm.selectionMode = LESSON_ACTIVITY_TYPE.ACTIVITY;
  }

  initModuleData()
    .then(() => {
      populateAndSortSectionList();
      paramsData.value.moduleData = moduleData.value;
    })
    .finally(() => {
      isReady.value = true;
    });
});

onUnmounted(() => {
  resetProcessing();
  // remove title if subpage is closed
  titles.value.pop();
});

const handleSaveButtonResetCompleted = () => {
  dataSaveButton.value.completed = false;
};

const handleSaveButtonResetError = () => {
  dataSaveButton.value.error = false;
};

const handleUpdateSelectedSection = (sectionId: string, isChecked?: boolean) => {
  const sectionActivityList = getActivitiesForSection(sectionId);
  if (isDefined(isChecked)) {
    //for checkbox selection
    if (isChecked) {
      vm.selectionMode = LESSON_ACTIVITY_TYPE.SECTION;
      if (!selectedSections.value.includes(sectionId.toString())) {
        selectedSections.value.push(sectionId.toString());
      }
      // select all activities in section
      sectionActivityList.forEach((activity: unknown) => {
        if (!selectedActivities.value.includes(activity.id.toString())) {
          selectedActivities.value.push(activity.id.toString());
        }
      });
    } else {
      selectedSections.value = selectedSections.value.filter((id) => id !== sectionId.toString());
      // unselect all activities in section
      sectionActivityList.forEach((activity: unknown) => {
        if (selectedActivities.value.includes(activity.id.toString())) {
          const index = selectedActivities.value.indexOf(activity.id.toString());
          selectedActivities.value.splice(index, 1);
        }
      });
      if (isEmpty(selectedSections.value)) {
        vm.selectionMode = "";
      }
    }
  } else {
    //for radio selection
    selectedSections.value = [sectionId.toString()];
    // select all activities in section
    selectedActivities.value = [];
    sectionActivityList.forEach((activity: unknown) => {
      selectedActivities.value.push(activity.id.toString());
    });
  }
  isEntireModuleSelected.value =
    availableSectionIds.value.length > 0 &&
    selectedSections.value.length === availableSectionIds.value.length &&
    availableSectionIds.value.every((sectionId) => selectedSections.value.includes(sectionId));
  vm.handleSectionsSelected(selectedSections.value);
  vm.handleActivitiesSelected(selectedActivities.value);
};

const handleSelectEntireModule = (isSelected: boolean) => {
  selectedSections.value = isSelected ? [...availableSectionIds.value] : [];
  selectedActivities.value = isSelected
    ? selectedSections.value.flatMap((sectionId) =>
        getActivitiesForSection(sectionId).map((activity) => activity.id.toString()),
      )
    : [];
  isEntireModuleSelected.value = isSelected;
  vm.selectionMode = isSelected ? LESSON_ACTIVITY_TYPE.SECTION : "";
  vm.handleSectionsSelected(selectedSections.value);
  vm.handleActivitiesSelected(selectedActivities.value);
};

const handleUpdateSelectedActivity = (activityId: string, isChecked?: boolean) => {
  if (isDefined(isChecked)) {
    if (isChecked) {
      vm.selectionMode = LESSON_ACTIVITY_TYPE.ACTIVITY;
      if (!selectedActivities.value.includes(activityId.toString())) {
        selectedActivities.value.push(activityId.toString());
      }
    } else {
      selectedActivities.value = selectedActivities.value.filter(
        (id) => id !== activityId.toString(),
      );
      if (isEmpty(selectedActivities.value)) {
        vm.selectionMode = "";
      }
    }
  } else {
    selectedActivities.value = [activityId.toString()];
  }
  vm.handleActivitiesSelected(selectedActivities.value);
};

const handleSectionToggle = (data: { sectionId: string; expanded: boolean }) => {
  if (paramsData.value.type !== ATTEMPTABLE_TYPE.ASSIGNMENT_LESSON) return;
  const sectionId = data.sectionId;
  const expanded = data.expanded;
  if (!expanded) {
    return;
  }

  if (paramsData.value.type !== "ASSIGNMENT_LESSON") {
    return;
  }

  store.setLoading(sectionId, true);
  const promise = isTeacher.value
    ? fetchAssignmentQuizActivityInfo(sectionId)
    : fetchAssignmentActivityCompletionInfo(sectionId);

  promise.then(() => {
    paramsData.value.moduleData = moduleData.value;
    store.setLoading(sectionId, false);
  });
};

const handleChoose = () => {
  if (isChooseButtonDisabled.value) return;
  removeDirty(dirtyId.value);

  if (props.params.selectionType === LessonSelectionTypeEnum.activityOrSection) {
    handleSectionOrActivityChosen();
    return;
  }

  if (props.params.selectionType === LessonSelectionTypeEnum.gamebook) {
    handleGameBookChosen();
    return;
  }
  if (props.params.selectionType === LessonSelectionTypeEnum.sectionOrLesson) {
    handleSectionOrLessonSelected();
    return;
  }

  if (props.params.selectionType === LessonSelectionTypeEnum.lesson) {
    handleLessonSelected();
    return;
  }

  if (props.params.template) {
    if (inLessonEditor.value) {
      addSectionsOrActivities();
      return;
    } else if (isFromAuthoringCopilot.value) {
      emit("lessonTemplateChosen", {
        lessonTemplateUuid: props.params.template?.uuid,
        activitiesSelected: vm.activitiesSelected,
        sectionsSelected: vm.sectionsSelected,
        onFail: onFail,
        onSuccess: handleSuccessCallback,
        onNext: () => {
          dataSaveButton.value.loading = false;
        },
      });
      return;
    } else {
      emit("lessonTemplateChosen", {
        lessonTemplateUuid: props.params.template?.uuid,
        activitiesSelected: isEmpty(vm.sectionsSelected) ? vm.activitiesSelected : [],
        sectionsSelected: showSectionCover.value ? vm.sectionsSelected : [],
        onFail: onFail,
        onSuccess: handleSuccessCallback,
        onNext: () => {
          dataSaveButton.value.loading = false;
        },
      });
      return;
    }
  }

  if (paramsData.value.type === "ASSIGNMENT_LESSON") {
    handleResourceSelected();
    return;
  }

  if (paramsData.value.type === "LESSON") {
    checkIfHasGoogleDriveAccess(
      showAppAvailabilityWarningModal,
      handleChoosePrimaryAction,
      () => {
        // empty
      },
      {
        requestData: [
          { uuid: paramsData.value.lesson.uuid, status: paramsData.value.lesson.status },
        ],
        googleDocumentRootFolderType: "LESSON",
      },
    );
    return;
  }
};

const handleChoosePrimaryAction = () => {
  if (paramsData.value.isContextCreateAssignment) {
    handleResourceSelected();
  }
};
const onSuccess = (backCurrentSubpage = true) => {
  onComplete();
  resetProcessing();
  removeDirty(dirtyId.value);
  if (backCurrentSubpage) {
    actionBack();
  }
};
const handleLessonSelected = () => {
  if (props.params.selectionType !== LessonSelectionTypeEnum.lesson) {
    return;
  }
  setLoading();
  resourceApi.checkResourceAssignable("LESSON", paramsData.value.lesson.uuid).then(
    (assignableResponse) => {
      if (assignableResponse.code !== "ok") {
        passiveApiFailHandler();
        onCancel();
        return;
      }
      validateResourceAssignable(assignableResponse, {
        onNotAssignable: () => {
          // Turn off the Choose button loading state immediately when the error modal is shown.
          onCancel();
          modal.showMessage("warning", {
            title: t("lesson_assignment_failed_title"),
            content: t("lesson_assignment_failed_unavailable_subtitle"),
            primaryBtnLabel: t("button_ok"),
            use_secondaryButton: false,
          });
        },
        onLoMissing: () => {
          onCancel();
          showAdaptiveSectionLoModal();
        },
        onPass: () => {
          emit("selected", {
            type: LessonSelectionEventType.lesson,
            data: {
              lessonUuid: paramsData.value.lesson.uuid,
              lessonId: Number(paramsData.value.id),
              status: paramsData.value.lesson.status,
              lessonType: "COURSE",
              title: paramsData.value.lesson.title,
              duplicatable: Boolean(paramsData.value.lesson.duplicatable),
              printable: Boolean(paramsData.value.lesson.printable),
              reattemptable: Boolean(paramsData.value.lesson.reattemptable),
            },
            onSuccess: onSuccess,
            onFail: onFail,
          } as AddLessonFromLibraryContentSubpageSelectionEvent);
        },
      });
    },
    (error) => {
      passiveApiFailHandler(error);
      onCancel();
    },
  );
};
const handleSectionOrLessonSelected = () => {
  if (props.params.selectionType !== LessonSelectionTypeEnum.sectionOrLesson) {
    return;
  }
  const attachEntireModule = isEntireModuleSelected.value || !hasSelectableSections.value;
  setLoading();
  if (attachEntireModule) {
    emit("selected", {
      type: LessonSelectionEventType.lesson,
      data: {
        lessonUuid: paramsData.value.lesson.uuid,
        lessonId: Number(paramsData.value.id),
        status: paramsData.value.lesson.status,
        lessonType: "COURSE",
        title: paramsData.value.lesson.title,
        duplicatable: Boolean(paramsData.value.lesson.duplicatable),
        printable: Boolean(paramsData.value.lesson.printable),
        reattemptable: Boolean(paramsData.value.lesson.reattemptable),
      },
      onSuccess: onSuccess,
      onFail: handleSectionOrLessonSelectionFail,
    } as AddLessonFromLibraryContentSubpageSelectionEvent);
  } else {
    emit("selected", {
      type: LessonSelectionEventType.section,
      data: {
        sectionId: selectedSections.value.map((s) => Number(s)),
        lessonUuid: paramsData.value.lesson.uuid,
        lessonId: Number(paramsData.value.id),
        title: paramsData.value.lesson.title,
        status: paramsData.value.lesson.status,
      },
      onSuccess: onSuccess,
      onFail: handleSectionOrLessonSelectionFail,
    } as AddLessonFromLibraryContentSubpageSelectionEvent);
  }
};

const handleGameBookChosen = () => {
  if (props.params.selectionType !== LessonSelectionTypeEnum.gamebook) {
    return;
  }
  setLoading();
  emit("selected", {
    type: LessonSelectionTypeEnum.gamebook,
    data: {
      gameBookId: Number(selectedGameBookId.value),
    },
    onSuccess: onSuccess,
    onFail: () => {
      onCancel();
    },
  } as AddLessonFromLibraryContentSubpageSelectionEvent);
};

const handleSectionOrActivityChosen = () => {
  if (props.params.selectionType !== LessonSelectionTypeEnum.activityOrSection) {
    return;
  }
  if (!isEmpty(selectedSections.value)) {
    setLoading();
    emit("selected", {
      type: LessonSelectionEventType.section,
      data: {
        sectionId: selectedSections.value.map((s) => Number(s)),
        lessonTemplateUuid: paramsData.value.template?.uuid,
        lessonUuid: paramsData.value.uuid,
        lessonId: Number(paramsData.value.id),
        title: paramsData.value.lesson.title,
        status: paramsData.value.lesson.status,
      },
      onFail: (error) => {
        if (error === "MAX") {
          handleMaxValidationError();
          return;
        }
        onFail();
      },
      onSuccess: onSuccess,
      onNext: () => {
        dataSaveButton.value.loading = false;
      },
    });
  } else if (!isEmpty(selectedActivities.value)) {
    setLoading();
    emit("selected", {
      type: LessonSelectionEventType.activity,
      data: {
        activityId: selectedActivities.value.map((s) => Number(s)),
        lessonTemplateUuid: paramsData.value.template?.uuid,
        lessonUuid: paramsData.value.uuid,
        lessonId: Number(paramsData.value.id),
        title: paramsData.value.lesson.title,
        status: paramsData.value.lesson.status,
      },
      onFail: (error) => {
        if (error === "MAX") {
          handleMaxValidationError();
          return;
        }
        onFail();
      },
      onSuccess: onSuccess,
      onNext: () => {
        dataSaveButton.value.loading = false;
      },
    });
  }
};

const handleResourceSelected = () => {
  emit("resourceSelected");
};

const handleSuccessCallback = () => {
  resetProcessing();
  removeDirty(dirtyId.value);
  dataSaveButton.value.completed = true;
  nextTick(() => {
    actionBack();
    if (
      typeof props.params.addResourceActionBack !== "undefined" &&
      typeof props.params.addResourceActionBack === "function"
    ) {
      props.params.addResourceActionBack();
    }
  });
};

const onFail = () => {
  dataSaveButton.value.error = true;
  dataSaveButton.value.loading = false;
};
const setLoading = () => {
  dataSaveButton.value.error = false;
  dataSaveButton.value.loading = true;
  dataSaveButton.value.completed = false;
};
const onComplete = () => {
  dataSaveButton.value.completed = true;
  dataSaveButton.value.loading = false;
};
const onCancel = () => {
  dataSaveButton.value.completed = false;
  dataSaveButton.value.loading = false;
  dataSaveButton.value.error = false;
};

const handleTabSelected = (index: number) => {
  selectedTab.value = tabs.value[index].key;
};

const populateAndSortSectionList = () => {
  lessonActivityList.value.forEach((activity: unknown) => {
    if (activity.type === LESSON_ACTIVITY_TYPE.SECTION) {
      sectionList.value.push(activity);
    }
  });
  return sectionList.value.sort((sectionA, sectionB) => {
    return sectionA.pageNo > sectionB.pageNo ? 1 : -1;
  });
};

const processPageSettingsFromRules = (rules: unknown[], openTeamActivityIds: string[]) => {
  // Filter rules for LESSON_ACTIVITY
  const pageRules = rules.filter((rule) => rule.conditionTargetType === "LESSON_ACTIVITY");

  const pageRulesMap = new Map();
  pageRules.forEach((rule) => {
    if (!pageRulesMap.has(rule.conditionTargetId)) {
      pageRulesMap.set(rule.conditionTargetId, []);
    }
    pageRulesMap.get(rule.conditionTargetId).push(rule);
  });

  const newPageSettingsMap: Record<string, unknown> = {};
  for (const [key, items] of pageRulesMap.entries()) {
    const pageSettings: Record<string, unknown> = {};

    items.forEach((item: unknown) => {
      if (RULE_CONDITION_TYPE.SCHEDULE === item.type) {
        Object.assign(pageSettings, {
          startDateTime: item.startDateTime,
          endDateTime: item.endDateTime,
        });
      }

      if (RULE_CONDITION_TYPE.ACCESSIBLE === item.type) {
        Object.assign(pageSettings, {
          accessible: item.activityAccessible,
        });
      }

      if (
        [
          RULE_CONDITION_TYPE.GROUP_ACCESS,
          RULE_CONDITION_TYPE.TEAM_ACCESS,
          RULE_CONDITION_TYPE.INDIVIDUAL_ACCESS,
        ].includes(item.type)
      ) {
        Object.assign(pageSettings, {
          groupWork: item.groupwork,
          restriction: item.restriction,
          restrictionSettings: item.restrictionSettings,
          teamAutoAllocate: isDefined(openTeamActivityIds) && openTeamActivityIds.includes(key),
        });
      }
    });

    newPageSettingsMap[key] = pageSettings;
  }

  pageSettingsMap.value = newPageSettingsMap;
};

const processRuleConditions = (ruleConditions: unknown[]) => {
  sectionPrerequisites.value = {};
  activityPrerequisites.value = {};
  sequentialActivityMap.value = {};

  ruleConditions.forEach((prerequisite) => {
    if (prerequisite.conditionTargetType === "LESSON_ACTIVITY") {
      const conditionTargetId = prerequisite.conditionTargetId;

      if (
        [
          RULE_CONDITION_TYPE.RESOURCE_COMPLETION,
          RULE_CONDITION_TYPE.QUIZ_SCORE,
          RULE_CONDITION_TYPE.GAME_AWARD,
          RULE_CONDITION_TYPE.GAME_CHOICE,
          RULE_CONDITION_TYPE.LEVEL_UP,
          RULE_CONDITION_TYPE.QUIZ_RANK,
        ].includes(prerequisite.type)
      ) {
        if (isSection(conditionTargetId)) {
          if (!sectionPrerequisites.value[conditionTargetId]) {
            sectionPrerequisites.value[conditionTargetId] = [];
          }
          sectionPrerequisites.value[conditionTargetId].push(prerequisite);
        } else {
          if (!activityPrerequisites.value[conditionTargetId]) {
            activityPrerequisites.value[conditionTargetId] = [];
          }
          activityPrerequisites.value[conditionTargetId].push(prerequisite);
        }
      }

      if (prerequisite.type === RULE_CONDITION_TYPE.PREVIOUS_COMPLETION) {
        sequentialActivityMap.value[conditionTargetId] =
          prerequisite.previousCompletionPrerequisite;
      }
    } else {
      previousCompletionPrerequisite.value = prerequisite.previousCompletionPrerequisite;
    }
  });
};

const fetchLesson = () => {
  const params = { uuid: props.params.uuid, additionalDetails: null };
  if (isFromLessonComponentAuthoringCopilot.value || isFromLessonActivityAuthoringCopilot.value) {
    Object.assign(params, { additionalDetails: true });
  }

  const fetchLessonDetailsApi = isCommunityResource.value
    ? cgLessonApi.fetchCGDetails
    : LessonApi.fetchLessonDetails;

  return fetchLessonDetailsApi(params)
    .then((response: unknown) => {
      if (response.code !== "ok") {
        passiveApiFailHandler(response.errorMap["0"]);
        return;
      }
      paramsData.value.lesson = response.data.formData;
      paramsData.value.communityResourceUuid = response.data.communityResourceUuid;

      showSectionCover.value = paramsData.value.lesson?.showSectionCover ?? true;
    })
    .catch(passiveApiFailHandler);
};

const fetchAssignmentMetadata = () => {
  return AssignmentApi.fetchAssignmentMetadata(props.params.assignmentUuid).then(
    (response: unknown) => {
      if (response.code === "ok") {
        metaData.value = response.data;
        processPageSettingsFromRules(metaData.value.rules, metaData.value.openTeamActivityIds);
        if (isTeacher.value) {
          overallProgress.value = {
            numStudentTotal: metaData.value.totalAssignee,
            numStudentCompleted: metaData.value.totalCompleted,
          };
        }
      } else {
        passiveApiFailHandler(response.errorMap["0"]);
      }
    },
    passiveApiFailHandler,
  );
};

const fetchAssignmentProgress = async () => {
  try {
    const progressResponse = await fetchWithSignedUrl(
      AssignmentApi.getAssignmentProgressSignedUrl,
      AssignmentApi.fetchAssignmentProgress,
      { assignmentUuid: metaData.value.uuid },
      { assignmentUuid: metaData.value.uuid, year: metaData.value.academicYear },
    );
    const progress = progressResponse.data;

    overallProgress.value = {
      numStudentTotal: progress.numStudentTotal,
      numStudentCompleted: progress.numStudentCompleted,
    };

    overallPageProgressMap.value = progress.pages.reduce((acc: unknown, pageProgress: unknown) => {
      acc[pageProgress.id] = pageProgress;
      return acc;
    }, {});
  } catch (error) {
    console.error("Error fetching assignment progress:", error);
  }
};

const fetchAssignmentPackageCompletionStatus = () => {
  return SearchAttemptApi.fetchModulePackageCompletionInfo(assignmentAttemptRequestData(null)).then(
    (response: unknown) => {
      if (response.code !== "ok") {
        passiveApiFailHandler(response.errorMap["0"]);
        return;
      }
      const data = response.data;
      packageProgress.value = {
        status: data.status,
        lastAttemptedDateTime: data.lastAttemptedDateTime,
        completedDateTime: data.completedDateTime,
      };
    },
    passiveApiFailHandler,
  );
};

const fetchAssignmentSectionCompletionInfo = () => {
  return SearchAttemptApi.fetchModuleSectionCompletionInfo(assignmentAttemptRequestData(null)).then(
    (response: unknown) => {
      if (response.code !== "ok") {
        passiveApiFailHandler(response.errorMap["0"]);
        return;
      }
      const completionMap = response.data;
      const sectionCompletionMap: Record<string, unknown> = {};

      completionMap.forEach((section: unknown) => {
        sectionCompletionMap[section.pageId] = {
          completed: !isNull(section.completedDateTime),
          completedDateTime: isNull(section.completedDateTime)
            ? null
            : DateTimeFormatter.formatDateTimeToDateTimeAmPm(section.completedDateTime),
          createdDate: isNull(section.createdDate)
            ? null
            : DateTimeFormatter.formatDateTimeToDateTimeAmPm(section.createdDate),
          markStatus: section.markStatus,
          activityType: section.activityType,
        };
      });
      pageCompletionInfoMap.value = { ...pageCompletionInfoMap.value, ...sectionCompletionMap };
    },
    passiveApiFailHandler,
  );
};

const fetchAssignmentActivityCompletionInfo = (sectionId: string) => {
  return SearchAttemptApi.fetchModuleSectionPageProgressInfo(
    assignmentAttemptRequestData(sectionId),
  ).then((response: unknown) => {
    if (response.code !== "ok") {
      passiveApiFailHandler(response.errorMap["0"]);
      return;
    }
    const completionMap = response.data;
    const sectionPageProgressMap: Record<string, unknown> = {};

    completionMap.forEach((activity: unknown) => {
      sectionPageProgressMap[activity.pageId] = {
        completed: !isNull(activity.completedDateTime),
        completedDateTime: isNull(activity.completedDateTime)
          ? null
          : DateTimeFormatter.formatDateTimeToDateTimeAmPm(activity.completedDateTime),
        createdDate: isNull(activity.createdDate)
          ? null
          : DateTimeFormatter.formatDateTimeToDateTimeAmPm(activity.createdDate),
        markStatus: activity.markStatus,
        activityType: activity.activityType,
      };
    });
    pageCompletionInfoMap.value = { ...pageCompletionInfoMap.value, ...sectionPageProgressMap };
  }, passiveApiFailHandler);
};

const fetchAssignmentQuizActivityInfo = (sectionId: string) => {
  return SearchAttemptApi.fetchModuleSectionQuizActivityInfo(
    assignmentAttemptRequestData(sectionId),
  ).then((response: unknown) => {
    if (response.code !== "ok") {
      passiveApiFailHandler();
      return;
    }
    const quizActivityInfoList = response.data;

    const sectionActivities = getActivitiesForSection(sectionId);

    const pageProgressMap: Record<string, unknown> = {};
    sectionActivities.forEach((activity: unknown) => {
      const current = overallPageProgressMap.value[activity.id] || {};
      pageProgressMap[activity.id] = {
        ...current,
        numPendingRelease: 0,
      };
    });

    quizActivityInfoList.forEach((activityQuizInfo: unknown) => {
      const current = overallPageProgressMap.value[activityQuizInfo.id] || {};
      pageProgressMap[activityQuizInfo.id] = {
        ...current,
        numPendingRelease: activityQuizInfo.numPendingRelease,
      };
    });

    overallPageProgressMap.value = {
      ...overallPageProgressMap.value,
      ...pageProgressMap,
    };
  }, passiveApiFailHandler);
};

const fetchAssignmentLessonData = () => {
  return AssignmentLessonApi.fetchAssignmentLesson({
    uuid: props.params.uuid,
    assignmentUuid: props.params.assignmentUuid,
  }).then((response: unknown) => {
    if (response && response.code === "ok") {
      paramsData.value.lesson = response.data;
      showSectionCover.value = paramsData.value.lesson?.showSectionCover ?? true;
    }
  }, passiveApiFailHandler);
};

const fetchRuleCondition = () => {
  if (!paramsData.value.lesson?.uuid) return Promise.resolve();

  return RuleConditionApi.fetchAllRuleConditions(
    paramsData.value.lesson.uuid,
    paramsData.value.lesson.status,
  ).then((response: unknown) => {
    if (response.code !== "ok") {
      passiveApiFailHandler(response.errorMap["0"]);
      return;
    }
    processRuleConditions(response.data);
  }, passiveApiFailHandler);
};

const fetchAssignmentMeetingLinksStatus = () => {
  return MeetingLinkApi.fetchAssignmentMeetingLinksStatus(props.params.assignmentUuid).then(
    (response: unknown) => {
      activityMeetingLinkStatusMap.value = response.data.activityMeetingLinkStatusMap;
      activityTeamMeetingLinkStatusMap.value = response.data.activityTeamMeetingLinkStatusMap;
    },
  );
};

const initModuleData = async () => {
  const promiseArray = [] as Promise[];

  switch (paramsData.value.type) {
    case "LESSON":
      promiseArray.push(fetchLesson());
      break;
    case "ASSIGNMENT_LESSON":
      promiseArray.push(
        fetchAssignmentMetadata(),
        fetchAssignmentLessonData(),
        fetchAssignmentMeetingLinksStatus(),
      );
      break;
  }

  return Promise.all(promiseArray)
    .then(() => fetchRuleCondition())
    .then(() => {
      if (paramsData.value.type === ATTEMPTABLE_TYPE.ASSIGNMENT_LESSON) {
        if (isTeacher.value) {
          return fetchAssignmentProgress();
        } else if (isStudent.value) {
          if (paramsData.value?.lesson?.passcodeEnabled) {
            return Promise.resolve();
          }

          return Promise.all([
            fetchAssignmentPackageCompletionStatus(),
            fetchAssignmentSectionCompletionInfo(),
          ]);
        }
      }
    }, passiveApiFailHandler);
};

const handleGameBookSelected = (gameBookId: string | null) => {
  selectedGameBookId.value = gameBookId;
  if (gameBookId) {
    removeDirty(dirtyId.value);
    return;
  }
  setDirty(dirtyId.value);
};

const handleResourceUnstarred = (unstarred: boolean) => {
  resourceUnstarred.value = unstarred;
};

const handleHidden = () => {
  if (resourceUnstarred.value) {
    emit("resourceUnstarred");
  }
};

const handleBack = () => {
  if (paramsData.value?.actionBack) {
    paramsData.value.actionBack();
  }

  if (resourceUnstarred.value) {
    emit("resourceUnstarred");
  }
};

const handleMaxValidationError = () => {
  dataSaveButton.value.errorMessage = props.params.isAttachingAttachment
    ? t("search_module_detail_max_attachments_error_message")
    : vm.getMaxSelectedErrorMessage();
  onFail();
};

const handleSectionOrLessonSelectionFail = (error: string) => {
  if (error === "MAX") {
    handleMaxValidationError();
    return;
  }
  onFail();
};

onUnmounted(() => {
  removeDirty(dirtyId.value);
});

vmExpose({
  handleMaxValidationError,
  dirtyId,
});
defineExpose(defaultExpose);
</script>

<style lang="scss">
@import "@styles/abstracts/variables";
@import "@styles/layout/breakpoint";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.search-module-detail-content-subpage {
  .main {
    .lesson-preview-info {
      margin-bottom: $spacing-05;
    }

    > .info-header {
      display: flex;
      flex-direction: column;
      gap: $spacing-03;
      margin: $spacing-05 0;

      &:has(.info-title:empty) {
        display: none;
      }

      .info-title {
        @include carbon--type-style("productive-heading-02");
      }

      .info-description {
        @include carbon--type-style("helper-text-01", true);
        color: $text-02;
      }
    }
  }

  .tabs {
    .tab-content {
      margin: $spacing-05 (-$spacing-05) 0;
      padding: $spacing-05;
      background-color: $ui-01;

      &#module-plan {
        padding-top: $spacing-03;
      }
    }
  }

  .rich-text {
    .rich-text-content {
      .attachment-placeholder {
        background-color: $ui-background;
        border-top: none;

        > .title {
          padding: 0;
          margin: 0;
          border: none;
        }
      }

      .sls-dynamic-link {
        cursor: auto;
      }
    }
  }
}
</style>
